/**
 * Listing API Methods
 *
 * Provides functions for querying rental listing information from the HotPads API.
 * All requests are routed through the /hotpads-api/api proxy to bypass PerimeterX.
 *
 * Note on paths:
 * - Paths are relative to the API proxy base (/hotpads-api/api)
 * - Client/SSR requests go to: /hotpads-api/api/v2/listing/...
 * - Next.js rewrites to: /api/hotpads-api/v2/listing/...
 * - Proxy forwards to: https://hotpads-api.hotpads.com/hotpads-api/api/v2/listing/...
 */

import { apiClient, ApiError, isPerimeterXBlockedError, isRequestTimeoutError } from '../client/apiClient';
import { fetchCsrfToken } from '../client/fetchCsrfToken';
import { fetchByCoords } from './listingByCoords';
import type { HpBreadcrumbDto } from '@zg-rentals/ts-hotpads-api-client';
import { LISTING_PHOTO_CAP } from '@/src/lib/utils/listingTransforms';
import { normalizeAmenities } from '@/src/lib/utils/normalizeAmenities';
import type { SummaryListingDetails } from '@/src/types/summaryListingDetails.type';
import type { CostsAndFees } from '@/src/types/costAndFees.type';
import type { ModelSummary } from '@/src/components/ListingCard/types';
import type { SurroundingAreaStats } from '@/src/types/listingDetails.type';

// ============================================================================
// Types
// ============================================================================

type ListingAreaBreadcrumb = {
  id?: string | number;
  resourceId?: string;
  name?: string;
  type?: string;
  uri?: string;
  uriV2?: string;
  keywords?: string;
};

export type ListingAreaSet = {
  specific?: ListingAreaBreadcrumb;
  breadcrumbs?: Array<ListingAreaBreadcrumb>;
  links?: Array<ListingAreaBreadcrumb>;
  schools?: Array<ListingDetailV3School>;
  districts?: Array<ListingAreaBreadcrumb>;
  surroundingAreaStats?: SurroundingAreaStats;
};

export type ListingPricingStats = Record<string, string>;

export type ListingUnitPriceDrop = {
  floorplanModelId: string;
  unitModelId: string;
  unitName: string;
  amount: number;
};

const LISTING_PRICING_STATS_TIMEOUT_MS = 1_500;

/**
 * Extended listing with additional runtime fields from API response
 * These fields exist in the API response but aren't in the legacy type definition
 */
export type ExtendedListing = SummaryListingDetails & {
  displayName?: string;
  has3dTour?: boolean;
  tourUrls?: Array<string>;
  tourLabels?: Array<string>;
  details?: {
    numUnits?: number | string | null;
  };
  lotIdEncoded?: string;
  modelSummary?: ModelSummary;
  areas?: ListingAreaSet;
  uriV2?: string;
  pricingStats?: ListingPricingStats;
  floorplans?: Array<{
    modelId?: string;
    units?: Array<{
      modelId?: string;
      name?: string;
      hasPriceDropped?: boolean;
      priceDrop?: number | string | null;
    }>;
  }>;
};

/**
 * Building with nested listings (when trimResponse: false)
 * API can return buildings containing multiple unit listings
 */
export type BuildingWithListings = ExtendedListing & {
  /** Nested listings array for multi-unit buildings */
  listings?: Array<ExtendedListing>;
};

/**
 * Union type for API response buildings array
 * Can be either flat listings or nested building structures
 */
export type BuildingOrListing = BuildingWithListings;

/**
 * Query parameters for fetching listings by map coordinates.
 * Filter params are sent as individual query params to the Java API.
 */
export type FetchByCoordsParams = {
  /** Minimum latitude of map bounds */
  minLat: number;
  /** Maximum latitude of map bounds */
  maxLat: number;
  /** Minimum longitude of map bounds */
  minLon: number;
  /** Maximum longitude of map bounds */
  maxLon: number;
  /** Number of listings to return (default: 500) */
  limit?: number;
  /** Offset for pagination (default: 0). Use (page - 1) * limit */
  offset?: number;
  /** Area ID to constrain results to specific area (when border is visible) */
  areas?: string;
  /** Search filters (beds, baths, price, etc.) */
  filter?: string;
  /** Sort order (e.g., 'price-asc', 'date-desc') */
  sortOrder?: string;
  /** Trim response to reduce payload size */
  trimResponse?: boolean;
  // --- Filter params (flat query params for byCoordsV2) ---
  lowPrice?: number;
  highPrice?: number;
  bedrooms?: string;
  bathrooms?: string;
  pets?: string;
  laundry?: string;
  amenities?: string;
  furnished?: boolean;
  propertyTypes?: string;
  minSqft?: number;
  maxSqft?: number;
  maxCreated?: string;
  listingTypes?: string;
  incomeRestricted?: boolean;
  seniorHousing?: boolean;
  studentHousing?: boolean;
  militaryHousing?: boolean;
  startOfAvailabilityDate?: string;
  endOfAvailabilityDate?: string;
  keywords?: string;
  requiresPhotos?: boolean;
  requiresPrice?: boolean;
  forRentByOwner?: boolean;
  acceptsSection8?: boolean;
  hasOffers?: boolean;
  acceptingApplications?: boolean;
  orderBy?: string;
  channels?: string;
  components?: string;
  visible?: string;
  /** Request the full photo collection per listing (populates medPhotoUrls). */
  includePhotosCollection?: boolean;
};

/**
 * Response from fetchByCoords endpoint
 */
export type FetchByCoordsResponse = {
  buildings?: Array<BuildingOrListing>;
  numBuildingsAvailable?: number;
  numListingsAvailable?: number;
  numListingsIncluded?: number;
  numUnits?: number;
};

/**
 * Query parameters for fetching a listing by malone lot id.
 */
export type FetchByMaloneLotIdEncodedParams = {
  active?: boolean;
  components?: string;
  isBuilding?: boolean;
  timeout?: number;
  optional?: string;
  type?: 'unit' | 'building';
  listingTypes?: string;
  unit?: string;
};

type FetchByMaloneLotIdEncodedRequestOptions = {
  signal?: AbortSignal;
  timeoutMs?: number;
  isExpectedError?: (error: ApiError) => boolean;
};

export type FetchByMaloneLotIdEncodedResponse = {
  breadcrumbs?: Array<HpBreadcrumbDto>;
  areas?: ListingAreaSet;
  availableUnits?: {
    rental?: Array<string | number>;
  };
  geo?: {
    lat?: number;
    lon?: number;
  };
  listing?: ExtendedListing;
  listings?: Array<ExtendedListing>;
  lotIdEncoded?: string;
  schools?: Array<{
    areaId?: number;
    areaSid?: string;
    geo?: {
      lat?: number;
      lon?: number;
    };
    highGrade?: string;
    id?: string;
    isPrivate?: boolean;
    lowGrade?: string;
    name?: string;
    rating?: number;
    resourceId?: string;
    type?: string;
    uriV2?: string;
  }>;
};

type HpApiResponse<T> = {
  data: T | null;
  session?: {
    sessionToken?: string;
    userToken?: string;
    csrfToken?: string;
    loggedIn?: boolean;
    verified?: boolean;
  } | null;
  error?: {
    reason?: string;
    message?: string;
  } | null;
};

export type ListingDetailV3TimeAgo = {
  date?: number;
  ago?: string;
};

export type ListingDetailV3Geo = {
  lat?: number;
  lon?: number;
  quad?: string;
};

export type ListingDetailV3Range = {
  min?: number | null;
  max?: number | null;
};

export type ListingDetailV3PhotoFormats = {
  thumb?: string;
  squarethumb?: string;
  medium?: string;
  mediumModel?: string;
  large?: string;
  [key: string]: string | undefined;
};

export type ListingDetailV3PhotoConfig = {
  baseUrl?: string;
  formats?: ListingDetailV3PhotoFormats;
};

export type ListingDetailV3MediaLink = {
  url?: string;
  label?: string | null;
};

export type ListingDetailV3Media = {
  photos?: Array<string>;
  photoIds?: Array<string>;
  photoHeroId?: string | null;
  heroId?: string | null;
  layouts?: Array<string>;
  videos?: Array<ListingDetailV3MediaLink>;
  tours?: Array<ListingDetailV3MediaLink>;
};

export type ListingDetailV3Resolved = {
  isActive?: boolean;
  lotId?: number;
  lotIdEncoded?: string;
  maloneLotIdEncoded?: string;
  listingAlias?: string;
  unit?: string | null;
  canonicalUrl?: string;
  resourceId?: string;
};

export type ListingDetailV3Summary = {
  beds?: ListingDetailV3Range;
  price?: ListingDetailV3Range;
  baths?: ListingDetailV3Range;
  sqft?: ListingDetailV3Range;
};

export type ListingDetailV3Details = Record<string, unknown> & {
  name?: string;
  title?: string;
  propertyType?: string;
  listingType?: string;
  rentalApplicationStatus?: string;
  numUnits?: number;
  fullDescription?: string;
  leaseTerms?: string;
  contactTimes?: string;
  specialOffer?: string;
  specialOffers?: Array<{ description?: string; endDate?: number }>;
};

export type ListingDetailV3Address = {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
};

export type ListingDetailV3Location = {
  address?: ListingDetailV3Address;
  geo?: ListingDetailV3Geo;
};

export type ListingDetailV3Area = ListingAreaBreadcrumb;

export type ListingDetailV3School = {
  areaId?: number;
  areaSid?: string;
  name?: string;
  address?: ListingDetailV3Address;
  phone?: string;
  type?: string;
  isPrivate?: boolean;
  lowGrade?: string;
  highGrade?: string;
  rating?: number;
  resourceId?: string;
  geo?: ListingDetailV3Geo;
  schoolDistrictAreaId?: number;
  schoolDistrictResourceId?: string;
};

export type ListingDetailV3Areas = {
  specific?: ListingDetailV3Area;
  breadcrumbs?: Array<ListingDetailV3Area>;
  links?: Array<ListingDetailV3Area>;
  schools?: Array<ListingDetailV3School>;
};

export type ListingDetailV3Amenities = {
  building?: Array<string>;
  unit?: Array<string>;
  someAvailableInSelectUnits?: boolean;
};

export type ListingDetailV3PetPolicy = {
  petType?: string;
  allowed?: boolean;
  petSize?: string;
  deposit?: number | string | null;
  oneTimeFee?: number | string | null;
  monthlyFee?: number | string | null;
  additionalFee?: number | string | null;
  maxNumberAllowed?: number | string | null;
  maxWeightAllowed?: number | string | null;
  comment?: string;
  restriction?: string;
};

export type ListingDetailV3FloorplanUnit = {
  lotIdEncoded?: string;
  maloneLotIdEncoded?: string;
  listingAlias?: string;
  canonicalUrl?: string;
  uriMalone?: string;
  hdpUrl?: string;
  unit?: string | null;
  modelId?: string;
  name?: string;
  beds?: number;
  baths?: number;
  sqft?: number;
  price?: ListingDetailV3Range;
  availabilityDate?: string;
  media?: ListingDetailV3Media;
};

export type ListingDetailV3Floorplan = {
  modelId?: string;
  name?: string;
  beds?: number;
  baths?: number;
  sqft?: number;
  price?: ListingDetailV3Range;
  availabilityDate?: string;
  media?: ListingDetailV3Media;
  units?: Array<ListingDetailV3FloorplanUnit>;
};

export type ListingDetailV3Contact = Record<string, unknown> & {
  restricted?: boolean;
  defaultInquiryMessage?: string;
  displayContactBox?: boolean;
  requiredFields?: Array<string>;
  incomeRestrictions?: Array<unknown>;
  message?: string;
  confirmationMessage?: string;
};

export type ListingDetailV3ListedBy = Record<string, unknown> & {
  companyName?: string;
  contactName?: string;
  contactPhone?: string;
  memberSince?: ListingDetailV3TimeAgo;
  webSite?: string;
  /** The building's real destination website — distinct from `webSite`, which is a HotPads click-tracking redirect URL. */
  buildingWebsite?: string;
  logoUrlCdn?: string | null;
  landlordPhotoUrl?: string | null;
  agentLicenseType?: string | null;
};

export type ListingDetailV3Popularity = {
  viewed?: number;
  inquiries?: number;
  favorites?: number;
  from?: ListingDetailV3TimeAgo;
};

export type ListingDetailV3History = {
  created?: ListingDetailV3TimeAgo;
  activated?: ListingDetailV3TimeAgo;
  updated?: ListingDetailV3TimeAgo;
  lastSynced?: ListingDetailV3TimeAgo;
};

export type ListingDetailV3Legal = {
  disclaimer?: string;
};

export type ListingDetailV3SimilarListing = {
  propertyType?: string;
  listingType?: string;
  listingAlias?: string;
  name?: string;
  title?: string;
  activated?: number;
  updated?: number;
  uriMalone?: string;
  photos?: ListingDetailV3PhotoConfig & {
    heroId?: string;
    photoIds?: Array<string>;
  };
  address?: ListingDetailV3Address;
  summary?: ListingDetailV3Summary;
  userItemTypes?: Array<string>;
  searchKeyword?: string;
  tags?: Array<string>;
  rentalApplicationStatus?: string;
  unitCount?: number;
  listedBy?: ListingDetailV3ListedBy;
  totalMonthlyPriceDisplayInfo?: Record<string, unknown>;
  newUnitCount?: number;
  priceDropCount?: number;
  specialOfferLabel?: string;
  contactForm?: ListingDetailV3Contact;
};

export type ListingDetailV3SimilarBuilding = {
  lotIdEncoded?: string;
  geo?: ListingDetailV3Geo;
  uri?: string;
  listings?: Array<ListingDetailV3SimilarListing>;
};

export type ListingDetailV3ModelTooltip = {
  modelId?: string | null;
  text?: string | null;
};

export type ListingDetailV3TotalMonthlyPriceDisplayInfo = {
  listingTooltip?: string | null;
  modelTooltips?: Array<ListingDetailV3ModelTooltip> | null;
  tooltipTexts?: Record<string, string | undefined>;
};

export type ListingDetailV3Data = {
  breadcrumbs?: Array<HpBreadcrumbDto>;
  resolved?: ListingDetailV3Resolved;
  photoConfig?: ListingDetailV3PhotoConfig;
  summary?: ListingDetailV3Summary;
  details?: ListingDetailV3Details;
  roomForRent?: unknown;
  location?: ListingDetailV3Location;
  areas?: ListingDetailV3Areas;
  media?: ListingDetailV3Media;
  amenities?: ListingDetailV3Amenities;
  petPolicy?: Array<ListingDetailV3PetPolicy>;
  petPolicies?: Array<ListingDetailV3PetPolicy>;
  tags?: Array<string>;
  floorplans?: Array<ListingDetailV3Floorplan>;
  costs?: CostsAndFees;
  costsAndFees?: CostsAndFees;
  totalMonthlyPriceDisplayInfo?: ListingDetailV3TotalMonthlyPriceDisplayInfo | null;
  contact?: ListingDetailV3Contact;
  listedBy?: ListingDetailV3ListedBy;
  popularity?: ListingDetailV3Popularity;
  userInteractions?: Array<string>;
  history?: ListingDetailV3History;
  legal?: ListingDetailV3Legal;
  similar?: Array<ListingDetailV3SimilarBuilding>;
};

export type FetchByAliasEncodedParams = FetchByMaloneLotIdEncodedParams & {
  validate?: boolean;
};

export type FetchByAliasEncodedResponse = FetchByMaloneLotIdEncodedResponse & {
  listing?: ExtendedListing;
};

export type ListingPhotoCollection = {
  medPhotoUrl?: string;
  medPhotoUrls: Array<string>;
  photoCount?: number;
};

export type ReportListingBody = {
  alias: string;
  type: 'notAvailable' | string;
  message?: string;
};

export type ProviderListingsPortfolioResponse = {
  buildings?: Array<BuildingOrListing>;
  listings?: Array<ExtendedListing>;
};

type SimilarListingsResponseItem = ExtendedListing & {
  lotIdEncoded?: string;
  uri?: string;
  listings?: Array<ExtendedListing>;
};

export type FetchSimilarListingsByMaloneLotIdEncodedParams = {
  maloneLotIdEncoded: string;
  unit?: string;
  listingTypes?: string;
  limit?: number;
  excludeInquiredListings?: boolean;
  components?: string;
  includePhotosCollection?: boolean;
};

const SIMILAR_LISTINGS_COMPONENTS = 'amenities,basic,useritem,quality,model,photos,pricing';

// ============================================================================
// API Methods
// ============================================================================

/** The requested lot was merged into another; the listing now lives under `newLotIdEncoded`. */
export class MovedLotError extends Error {
  newLotIdEncoded: string;

  constructor(newLotIdEncoded: string) {
    super(`Listing lot moved to ${newLotIdEncoded}`);
    this.name = 'MovedLotError';
    this.newLotIdEncoded = newLotIdEncoded;
  }
}

/** The requested lot no longer resolves and the API did not provide a replacement listing. */
export class LotIdNotFoundError extends Error {
  constructor() {
    super('Listing lot not found');
    this.name = 'LotIdNotFoundError';
  }
}

/**
 * Fetch a listing payload by malone lot id.
 *
 * Used by PDP/HDP SSR to resolve canonical area breadcrumbs from a listing URL.
 */
export const fetchByMaloneLotIdEncoded = async (
  maloneLotIdEncoded: string,
  params?: FetchByMaloneLotIdEncodedParams,
  requestOptions?: FetchByMaloneLotIdEncodedRequestOptions,
): Promise<FetchByMaloneLotIdEncodedResponse> => {
  const response = await apiClient<{
    data: FetchByMaloneLotIdEncodedResponse;
    status?: string;
    message?: string;
  }>({
    path: '/v2/listing/byMaloneLotIdEncodedV2',
    queryParams: {
      components: 'all',
      timeout: 100,
      optional:
        'responsiveness,company_photo,popularity,listedby,pricing_stats,lotIdReviews,schools,company,floorplan',
      type: 'unit',
      listingTypes: 'corporate,rental,room,sublet',
      unit: '',
      ...params,
      maloneLotIdEncoded,
    },
    ...(requestOptions?.signal ? { signal: requestOptions.signal } : {}),
    ...(requestOptions?.timeoutMs === undefined ? {} : { timeout: requestOptions.timeoutMs }),
    isExpectedError: requestOptions?.isExpectedError ?? isPerimeterXBlockedError,
  });
  if (response.status === 'MALONE_CHANGED' && response.message) {
    throw new MovedLotError(response.message);
  }

  if (response.status === 'LISTING_NOT_FOUND') {
    throw new ApiError('Listing not found in /v2/listing/byMaloneLotIdEncodedV2 response', {
      status: 404,
      data: response,
    });
  }

  if (response.status === 'LOT_ID_NOT_FOUND') {
    throw new LotIdNotFoundError();
  }

  if (!response.data) {
    throw new ApiError('Empty data in /v2/listing/byMaloneLotIdEncodedV2 response', { data: response });
  }

  return response.data;
};

export type FetchListingDetailV3Options = {
  unit?: string | null;
  signal?: AbortSignal;
};

type FetchListingPricingStatsOptions = {
  signal?: AbortSignal;
};

export const fetchListingDetailV3 = async (
  maloneLotIdEncoded: string,
  options: FetchListingDetailV3Options = {},
): Promise<ListingDetailV3Data> => {
  const unit = options.unit?.trim() || null;
  const response = await apiClient<HpApiResponse<ListingDetailV3Data>>({
    path: '/web/v3/listings/detail',
    queryParams: {
      maloneLotIdEncoded,
      unit,
    },
    signal: options.signal,
    followApiRedirects: false,
    isExpectedError: isPerimeterXBlockedError,
  });

  if (!response.data) {
    throw new ApiError('Empty data in /web/v3/listings/detail response', { data: response });
  }

  return response.data;
};

export const fetchListingPricingStats = async (
  maloneLotIdEncoded: string,
  unit?: string | null,
  options: FetchListingPricingStatsOptions = {},
): Promise<ListingPricingStats | null> => {
  const response = await fetchByMaloneLotIdEncoded(
    maloneLotIdEncoded,
    {
      components: 'pricing',
      optional: 'pricing_stats',
      type: 'unit',
      unit: unit?.trim() || '',
    },
    {
      signal: options.signal,
      timeoutMs: LISTING_PRICING_STATS_TIMEOUT_MS,
      isExpectedError: (error) => isPerimeterXBlockedError(error) || isRequestTimeoutError(error),
    },
  );

  return response.listings?.[0]?.pricingStats ?? null;
};

export type ListingDetailV3WithPricingStats = {
  detail: ListingDetailV3Data;
  pricingStats: ListingPricingStats | null;
  priceDrops: Array<ListingUnitPriceDrop>;
};

type ListingDetailSupplement = Pick<ListingDetailV3WithPricingStats, 'pricingStats' | 'priceDrops'>;

function extractUnitPriceDrops(listing: ExtendedListing | undefined): Array<ListingUnitPriceDrop> {
  return (listing?.floorplans ?? []).flatMap((floorplan) =>
    (floorplan.units ?? []).flatMap((unit) => {
      const amount = Number(unit.priceDrop ?? 0);
      if (unit.hasPriceDropped !== true || !Number.isFinite(amount) || amount <= 0) return [];

      return [
        {
          floorplanModelId: floorplan.modelId ?? '',
          unitModelId: unit.modelId ?? '',
          unitName: unit.name ?? '',
          amount,
        },
      ];
    }),
  );
}

function extractListingDetailSupplement(response: FetchByMaloneLotIdEncodedResponse): ListingDetailSupplement {
  const listings = response.listings?.length ? response.listings : response.listing ? [response.listing] : [];
  const pricingStats = listings.reduce<ListingPricingStats | null>(
    (stats, listing) => (listing.pricingStats ? { ...(stats ?? {}), ...listing.pricingStats } : stats),
    null,
  );

  return {
    pricingStats,
    priceDrops: listings.flatMap(extractUnitPriceDrops),
  };
}

async function fetchOptionalListingDetailSupplement(
  maloneLotIdEncoded: string,
  options: FetchListingDetailV3Options,
): Promise<ListingDetailSupplement> {
  try {
    const response = await fetchByMaloneLotIdEncoded(
      maloneLotIdEncoded,
      {
        components: 'all',
        optional: 'floorplan,pricing_stats',
        type: 'unit',
        unit: options.unit?.trim() || '',
      },
      {
        signal: options.signal,
        timeoutMs: LISTING_PRICING_STATS_TIMEOUT_MS,
        isExpectedError: (error) => isPerimeterXBlockedError(error) || isRequestTimeoutError(error),
      },
    );
    return extractListingDetailSupplement(response);
  } catch (error) {
    if (options.signal?.aborted) throw error;
    return { pricingStats: null, priceDrops: [] };
  }
}

export const fetchCostsAndFees = async (
  listingAlias: string,
  options: { signal?: AbortSignal } = {},
): Promise<CostsAndFees> => {
  const response = await apiClient<HpApiResponse<CostsAndFees>>({
    path: '/v2/costsAndFees/getCostsAndFees',
    queryParams: { listingAlias },
    signal: options.signal,
  });
  if (!response.data) {
    throw new ApiError('Empty data in /v2/costsAndFees/getCostsAndFees response', { data: response });
  }
  return response.data;
};

export const fetchListingDetailV3WithPricingStats = async (
  maloneLotIdEncoded: string,
  options: FetchListingDetailV3Options = {},
): Promise<ListingDetailV3WithPricingStats> => {
  const [detail, supplement] = await Promise.all([
    fetchListingDetailV3(maloneLotIdEncoded, options),
    fetchOptionalListingDetailSupplement(maloneLotIdEncoded, options),
  ]);

  return { detail, ...supplement };
};

export const fetchByAliasEncoded = async (
  alias: string,
  params?: FetchByAliasEncodedParams,
): Promise<FetchByAliasEncodedResponse> => {
  const response = await apiClient<{ data?: FetchByAliasEncodedResponse }>({
    path: '/v2/listing/byAliasV2',
    queryParams: {
      components: 'all',
      timeout: 100,
      optional:
        'responsiveness,company_photo,popularity,listedby,pricing_stats,lotIdReviews,schools,company,floorplan',
      type: 'unit',
      unit: '',
      validate: false,
      ...params,
      alias,
    },
  });
  // An unknown alias comes back as a 200 envelope with no `data` at all, not an error status.
  // Resolved-empty rather than the throw `fetchByMaloneLotIdEncoded` uses, so callers can tell
  // "no such alias" from an outage.
  return response.data ?? {};
};

export const fetchListingPhotoCollectionByMaloneLotIdEncoded = async (
  maloneLotIdEncoded: string,
  params?: Pick<FetchByMaloneLotIdEncodedParams, 'listingTypes' | 'timeout' | 'type' | 'unit'>,
): Promise<ListingPhotoCollection> => {
  const response = await apiClient<{
    data: FetchByMaloneLotIdEncodedResponse & { listing?: ExtendedListing };
  }>({
    path: '/v2/listing/byMaloneLotIdEncodedV2',
    queryParams: {
      components: 'basic,photos',
      timeout: 100,
      type: 'unit',
      unit: '',
      listingTypes: 'corporate,rental,room,sublet',
      ...params,
      maloneLotIdEncoded,
    },
  });
  const firstListing = response.data?.listings?.[0] ?? response.data?.listing;
  const medPhotoUrls = firstListing?.medPhotoUrls?.length
    ? firstListing.medPhotoUrls
    : firstListing?.medPhotoUrl
      ? [firstListing.medPhotoUrl]
      : [];

  return {
    medPhotoUrl: firstListing?.medPhotoUrl ?? medPhotoUrls[0],
    medPhotoUrls: medPhotoUrls.slice(0, LISTING_PHOTO_CAP),
    photoCount: firstListing?.photoCount,
  };
};

export const fetchSimilarListingsByMaloneLotIdEncoded = async (
  params: FetchSimilarListingsByMaloneLotIdEncodedParams,
): Promise<Array<ExtendedListing>> => {
  const response = await apiClient<{ data: Array<SimilarListingsResponseItem> }>({
    path: '/v2/listing/similar/byMaloneLotIdEncodedV2',
    queryParams: {
      components: SIMILAR_LISTINGS_COMPONENTS,
      includePhotosCollection: true,
      limit: 10,
      excludeInquiredListings: false,
      ...params,
    },
  });
  return (response.data ?? []).flatMap((building) => {
    const listings =
      building.listings && building.listings.length > 0 ? building.listings : [building];

    return listings.map((listing) => {
      const amenities = normalizeAmenities(
        (listing.amenities ?? building.amenities) as unknown as
          | Record<string, unknown>
          | null
          | undefined,
      );

      return {
        ...listing,
        ...(amenities ? { amenities: amenities as unknown as ExtendedListing['amenities'] } : {}),
        geo: listing.geo ?? building.geo,
        maloneLotIdEncoded: listing.maloneLotIdEncoded ?? building.lotIdEncoded,
        uriV2: listing.uriV2 ?? listing.uriMalone ?? building.uri,
      };
    });
  });
};

const flattenPortfolioListings = (
  payload: ProviderListingsPortfolioResponse | null | undefined,
): Array<ExtendedListing> => {
  const listings = payload?.listings ?? [];
  const buildings = payload?.buildings ?? [];

  return [
    ...listings,
    ...buildings.flatMap((building) =>
      building.listings && building.listings.length > 0 ? building.listings : [building],
    ),
  ];
};

export const fetchProviderListingsPortfolio = async (
  aliasEncoded: string,
): Promise<Array<ExtendedListing>> => {
  const response = await apiClient<{ data: ProviderListingsPortfolioResponse }>({
    path: '/v2/listing/nearby/portfolioProperties',
    queryParams: {
      alias: aliasEncoded,
    },
  });

  return flattenPortfolioListings(response.data);
};

export const reportListing = async (body: ReportListingBody): Promise<void> => {
  const csrfToken = await fetchCsrfToken();
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  await apiClient<unknown>({
    method: 'PUT',
    path: '/v2/listing/report',
    body,
    headers,
  });
};

// ============================================================================
// HomeHub Types & Endpoint
// ============================================================================

type HomeHubBuilding = {
  lotIdEncoded?: string;
  geo?: { lat: number; lon: number };
  uri?: string;
  city?: string;
  neighborhoods?: Array<string>;
  listings?: Array<SummaryListingDetails>;
};

type HomeHubCollection = {
  collectionType: string;
  title: string;
  name: string;
  resourceId?: string;
  buildingGroupDtos?: {
    buildings?: Array<HomeHubBuilding>;
  };
};

type HomeHubResponse = {
  data: Array<HomeHubCollection>;
};

/**
 * Fetch personalized homeHub collections (featured rentals).
 * Returns an array of collections, each containing building listings.
 */
export const fetchHomeHub = async (options?: {
  headers?: Record<string, string>;
  ip?: string;
  userAgent?: string;
}): Promise<Array<HomeHubCollection>> => {
  const response = await apiClient<HomeHubResponse>({
    path: '/v2/homeHub',
    headers: options?.headers,
    ip: options?.ip,
    userAgent: options?.userAgent,
  });
  return response.data;
};

// ============================================================================
// DotMap Types & Endpoint
// ============================================================================

export type DotMapDot = {
  lat: number;
  lon: number;
  lotId: string;
};

export type FetchDotMapParams = Pick<
  FetchByCoordsParams,
  'minLat' | 'maxLat' | 'minLon' | 'maxLon'
> &
  Partial<
    Omit<
      FetchByCoordsParams,
      | 'minLat'
      | 'maxLat'
      | 'minLon'
      | 'maxLon'
      | 'areas'
      | 'limit'
      | 'offset'
      | 'components'
      | 'includePhotosCollection'
    >
  >;

/**
 * Fetch all building dots for the given viewport bounds.
 * Returns lightweight lat/lon/lotId points for the density dot layer.
 */
export const fetchDotMap = async (
  params: FetchDotMapParams,
  signal?: AbortSignal,
): Promise<Array<DotMapDot>> => {
  const response = await apiClient<{ data: { buildings: Array<DotMapDot> } }>({
    path: '/v2/listing/dotMap',
    queryParams: {
      ...params,
      limit: 16,
    },
    signal,
  });
  return response.data?.buildings ?? [];
};

/**
 * Listing API namespace
 * Provides organized access to all listing-related endpoints
 */
export const listing = {
  fetchByCoords,
  fetchByMaloneLotIdEncoded,
  fetchListingDetailV3,
  fetchListingDetailV3WithPricingStats,
  fetchListingPricingStats,
  fetchCostsAndFees,
  fetchByAliasEncoded,
  fetchListingPhotoCollectionByMaloneLotIdEncoded,
  fetchSimilarListingsByMaloneLotIdEncoded,
  fetchProviderListingsPortfolio,
  fetchDotMap,
  fetchHomeHub,
  reportListing,
};
